import React, { useContext, useEffect, useState } from "react";
import SearchLocationsWidget from "components/SearchLocationWidget";
import { LOCATION_FILTER_ACTIONS } from "lookup";
import { JobsContext, SearchContext } from "context/providers";
import { X } from "lucide-react";
import { cn } from "utils";

const SearchLocations = ({ handleLocationChange }) => {
  return (
    <div>
      <SearchLocationsWidget
        locationChange={(location) => {
          handleLocationChange(LOCATION_FILTER_ACTIONS.ADD, { ...location });
        }}
      />
    </div>
  );
};

const SearchLocationChip = ({ value, onClose }) => {
  return (
    <div className="rounded-md px-2 py-1 flex items-center text-xs font-semibold gap-1 bg-slate-100 text-slate-700">
      {value}

      <button
        onClick={onClose}
        className="rounded-full flex justify-center items-center w-auto text-inherit hover:bg-slate-200 aspect-square"
      >
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
};

const SpecificLocationRefinement = () => {
  const {
    locationFilter,
    locationFilterChange,
    refineLocationCountries,
    setProximitySearch,
  } = useContext(SearchContext);

  const { jobOpp } = useContext(JobsContext);

  const [chips, setChips] = useState([]);
  const [radius, setRadius] = useState();

  const handleLocationChange = (action, location) => {
    locationFilterChange(action, location);
  };

  useEffect(() => {
    if (
      radius &&
      locationFilter.cityName &&
      radius !== locationFilter?.aroundRadius
    ) {
      const timeout = setTimeout(() => {
        setProximitySearch(radius);
      }, 400);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  // job has been set we need to set the default aroundRadius value from the context location filter
  useEffect(() => {
    if (
      jobOpp?.id &&
      typeof locationFilter?.aroundRadius === "number" &&
      radius !== locationFilter?.aroundRadius
    ) {
      setRadius(locationFilter?.aroundRadius);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp?.id, locationFilter?.aroundRadius]);

  function getChips(location) {
    const activeLocations = [];

    if (location.countryName) {
      activeLocations.push({
        label: `Country: ${location.countryName}`,
        attribute: "countryName",
      });
    }

    if (location.stateName) {
      activeLocations.push({
        label: `State: ${location.stateName}`,
        attribute: "stateName",
      });
    }

    if (location.cityName) {
      activeLocations.push({
        label: `City: ${location.cityName}`,
        attribute: "cityName",
      });
    }

    return activeLocations;
  }

  useEffect(() => {
    const locationChips = getChips(locationFilter);
    setChips([...locationChips]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter]);

  const handleDeleteAll = () => {
    handleLocationChange(LOCATION_FILTER_ACTIONS.REMOVE, "countryName");
  };

  return (
    <div className="flex flex-col gap-3">
      {!!handleLocationChange && (
        <>
          <SearchLocations
            handleLocationChange={handleLocationChange}
            setProximitySearch={setProximitySearch}
          />
        </>
      )}

      {chips.length > 0 && (
        <div className={cn("flex flex-wrap gap-1")}>
          {chips.map(({ label, attribute }) => (
            <SearchLocationChip
              key={label}
              value={label}
              onClose={() => {
                handleLocationChange(LOCATION_FILTER_ACTIONS.REMOVE, attribute);
              }}
              isActive
            />
          ))}
        </div>
      )}

      <div className="flex items-end justify-between flex-wrap">
        <div>
          {locationFilter.cityName && (
            <div className="flex">
              <input
                id="radius"
                type="checkbox"
                checked={typeof locationFilter.aroundRadius === "number"}
                onChange={() => {
                  if (typeof locationFilter.aroundRadius === "number") {
                    setRadius(null);
                    setProximitySearch(null);
                    return;
                  }

                  setRadius(50);
                  setProximitySearch(50);
                }}
              />
              <label className="ml-3" htmlFor="radius">
                Expand radius
              </label>
            </div>
          )}

          {locationFilter.cityName &&
            typeof locationFilter.aroundRadius === "number" && (
              <div className="flex pl-7 items-center">
                <label htmlFor="radius">Miles</label>
                <input
                  style={{
                    maxWidth: "100px",
                  }}
                  id="radius"
                  type="text"
                  placeholder="radius"
                  className="border-2 border-sky-600 rounded-md p-1 max-w-20 ml-2"
                  value={radius}
                  onInput={({ target }) => {
                    const inputValue = target.value.replace(/\D/g, "");

                    if (inputValue !== "" && Number(inputValue) > 0) {
                      setRadius(Number(inputValue));
                    } else {
                      setRadius("");
                    }
                  }}
                />
              </div>
            )}
        </div>

        <div className="flex flex-col items-end">
          {chips.length > 0 && (
            <button
              className="text-sky-600 font-semibold text-sm underline hover:no-underline"
              onClick={handleDeleteAll}
            >
              Clear All
            </button>
          )}

          {!!jobOpp.location && (
            <button
              className="text-sky-600 font-semibold text-sm underline hover:no-underline"
              onClick={() => {
                locationFilterChange(LOCATION_FILTER_ACTIONS.ADD, {
                  countryName: jobOpp.location.countryName,
                  stateName: jobOpp.location.stateName,
                  cityName: jobOpp.location.cityName,
                  aroundLatLng:
                    jobOpp.location.latitude && jobOpp.location.longitude
                      ? `${jobOpp.location.latitude}, ${jobOpp.location.longitude}`
                      : undefined,
                  aroundRadius: 50,
                });
                refineLocationCountries([jobOpp.location.countryName]);
              }}
            >
              Reset Location
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecificLocationRefinement;
